/**
 * useBenefits — React Query hook for `ContentReaderPort.listBenefits`
 * (029 US3 / FR-008).
 *
 * Wraps the active `ContentReaderPort` (resolved via {@link useContent}) in a
 * `useQuery` call. Drives the `/jasenedut` surface (`JasenedutRoute.tsx`, T031),
 * which renders the published `benefit` entries grouped by category. Mirrors the
 * established member-read hook pattern (`useVideoListings`, `useCuratedCards`):
 * the adapter stays a plain fetcher (D-046), this hook owns cache + loading
 * semantics, and the route maps React Query's state matrix onto skeleton /
 * boundary-throw / empty / populated.
 *
 * queryKey: `['content', 'benefits', limit, locale]` — flat, with `limit` and
 * `locale` included so distinct requests do not alias in the cache.
 *
 * Authoritative spec references:
 *   - `specs/029-cms-driven-member-surfaces/contracts/trpc-content-procedures.md`.
 *   - `docs/governance/decisions/D-046.md` D-046 (React Query integration).
 *   - `apps/web/src/content/ContentReaderPort.ts` (the port being wrapped).
 */

import { type UseQueryResult, useQuery } from '@tanstack/react-query';

import type { Benefit } from '../ContentReaderPort.js';
import { CONTENT_STALE_TIME } from './queryConfig.js';
import { useContent } from './useContent.js';

/**
 * Options for {@link useBenefits}. `limit` is a soft cap on the small
 * `/jasenedut` set; `locale` is forwarded verbatim to the port (fi default at
 * the adapter, D-051). See `useVideoListings` for the `?: T | undefined`
 * rationale under `exactOptionalPropertyTypes`.
 */
export interface UseBenefitsOptions {
  readonly limit?: number | undefined;
  readonly locale?: string | undefined;
}

/**
 * Fetch the `/jasenedut` benefits ordered by `order` ascending. Returns the
 * standard React Query result; `JasenedutRoute` renders a skeleton while
 * pending, throws on `isError` (caught by the route's `PortErrorBoundary`), the
 * empty state when `data` is `[]`, and the category-grouped list once `data`
 * resolves.
 */
export function useBenefits(
  options: UseBenefitsOptions = {},
): UseQueryResult<readonly Benefit[], Error> {
  const { limit, locale } = options;
  const port = useContent();

  return useQuery<readonly Benefit[], Error>({
    queryKey: ['content', 'benefits', limit, locale],
    queryFn: () => port.listBenefits({ limit, locale }),
    staleTime: CONTENT_STALE_TIME,
  });
}
